import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import numeral from 'numeral';

import { setPortal } from 'modules/portal';
import { abusingIpReport } from 'lib/api/abusing';

import NewButton from 'components/molecules/NewButton';
import NewTableContainer from 'components/molecules/NewTable';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import IconAreaWrap from 'components/molecules/MbTable/IconAreaWrap';
import FilterCollection from 'components/organisms/FilterCollection';
import NewCommonFilter from 'components/organisms/NewCommonFilter';

const Statistics = ({ handleClickSnackbar }) => {
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [initialization, setInitialization] = useState(false);
  const initParams = {
    sort: 'asc',
    order: 'IP',
    sDate: moment().add(-7, 'day').format('YYYYMMDD'),
    eDate: moment().format('YYYYMMDD'),
    clickCnt: '5',
  };
  const [tempParams, setTempParams] = useState(initParams);
  const [params, setParams] = useState(initParams);
  const list = {
    clickCnt: [
      { label: '5회', value: '5' },
      { label: '10회', value: '10' },
      { label: '20회', value: '20' },
      { label: '50회', value: '50' },
    ],
  };
  const initLoading = true;
  const [loading, setLoading] = useState(initLoading);

  /**
   * 필터 초기화 후 값을 바꿈
   */
  useEffect(() => {
    if (initialization === true) {
      setInitialization(false);
    } // eslint-disable-next-line
  }, [initialization]);

  /**
   * 필터 초기화
   */
  const handleOnReset = () => {
    if (JSON.stringify(params) !== JSON.stringify(initParams)) {
      setInitialization(true);
      setParams(initParams);
      setTempParams(initParams);
    }
  };

  const getTempsParam = (newParams) => {
    let newTempParams = {
      ...newParams,
    };
    if (JSON.stringify(tempParams) !== JSON.stringify(newParams)) {
      if (tempParams.sDate !== newParams.sDate) {
        newTempParams = {
          ...newTempParams,
          eDate: moment(newParams.sDate).add(7, 'day').format('YYYYMMDD'),
        };
        handleClickSnackbar({
          message: t('날짜 보정이 되었습니다'),
          option: {
            type: 'info',
          },
        });
      } else if (tempParams.eDate !== newParams.eDate) {
        newTempParams = {
          ...newTempParams,
          sDate: moment(newParams.eDate).add(-7, 'day').format('YYYYMMDD'),
        };
        handleClickSnackbar({
          message: t('날짜 보정이 되었습니다'),
          option: {
            type: 'info',
          },
        });
      }
      setTempParams(newTempParams);
    }
  };

  /**
   * 페이지 파라미터를 관리하는 params을 수정하는 함수
   */
  const getParam = () => {
    if (JSON.stringify(params) !== JSON.stringify(tempParams)) {
      setParams(tempParams);
    }
  };

  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  // header 및 body 컬럼 커스텀
  const customized = {
    IP: { orderFlag: false },
    AVAL_CLICK_CNT: {
      makeHead: (headsMap) => {
        return t('클릭(유효/무효)');
      },
      makeBody: (rowObj, headsMap) => {
        let resultComponent;

        const showWindowUrl =
          '/report/abusing/ipreport/click?ip=' +
          rowObj['IP'] +
          '&type=click&sDate=' +
          params.sDate +
          '&eDate=' +
          params.eDate +
          '&isPop=1';

        const bodyTitle =
          numeral(
            Number(rowObj['AVAL_CLICK_CNT']) + Number(rowObj['IVD_CLICK_CNT'])
          ).format() +
          ' (' +
          numeral(rowObj['AVAL_CLICK_CNT']).format() +
          ' / ' +
          numeral(rowObj['IVD_CLICK_CNT']).format() +
          ')';
        resultComponent = (
          <IconAreaWrap
            text={bodyTitle}
            icon={rowObj['IP'] !== 'total' && 'faExternalLink'}
            toolTipTitle={t('해당 IP의 클릭 내역')}
            onClick={(e) => handlePortalUrl(showWindowUrl)}
          />
        );
        return resultComponent;
      },
      headStyle: { width: 250 },
      orderFlag: false,
    },
    IVD_CLICK_CNT: { merged: true },
  };

  const tableCallBack = (state) => {
    if (state === 'success') {
      setLoading(false);
    }
  };

  const toolTipText = (
    <>
      <p>
        클릭시 "ip+난수, 매체지면, 캠페인, 타겟팅기법, 상품" 이 같을 경우 1시간
        동안 무효클릭으로 인정
      </p>
      <p>SDK의 경우 24시간으로 제한</p>
      <p>특정매체의 경우 ip+난수, 매체지면, 광고주로 적용</p>
      <p>일자별 5회 이상 모비온 광고클릭이 일어난 리스트</p>
      <p>유효 : 실제 과금된 클릭 수</p>
      <p>무효 : 한 유저가 1시간내 같은 광고를 재 클릭시 무효처리된 클릭 수</p>
      <p>
        일일 데이터를 수집 후 통계를 내기 때문에 전일 날짜까지만 표기가
        가능합니다.
      </p>
      <p>데이터 유지기간 : 30일</p>
    </>
  );

  return (
    <>
      <NewFilterWrap handleOnReset={handleOnReset}>
        <NewCommonFilter
          initialization={initialization}
          params={tempParams}
          getParam={getTempsParam}
          usePltfomTpCodeFilter={false}
          useDisplayFilter={false}
          useExternalFilter={false}
          useDatePresets={false}
          toolTip={t('날짜의 범위는 7일 단위로 고정되어있습니다.')}
        />
        <FilterCollection
          label={t('클릭')}
          name="clickCnt"
          params={tempParams}
          value={tempParams.clickCnt}
          type="select"
          list={list.clickCnt}
          callBack={getTempsParam}
          labelKey="label"
          valueKey="value"
        />
        <NewButton type="filterButton" onClick={getParam} disableRipple>
          {t('검색')}
        </NewButton>
      </NewFilterWrap>
      <NewTableContainer
        params={params}
        loading={loading}
        callBack={tableCallBack}
        getParam={getParam}
        dataReqPromise={abusingIpReport}
        customized={customized}
        toolTip={toolTipText}
        useFetch={false}
      />
    </>
  );
};

export default Statistics;
